<template>
  <div class="wrapper">
    <span
      v-if="typeItems !== 'tour'"
      class="wrapper__btn-left"
      @click="changeSlideLeft"
      ref="iconLeft"
    >
      <SvgBtnArrowSvg />
    </span>
    <span
      v-if="typeItems !== 'tour'"
      class="wrapper__btn-right"
      @click="changeSlideRight"
      ref="iconRight"
    >
      <SvgBtnArrowSvg :right="true" />
    </span>
    <div
      class="carousel"
      @mousemove="dragging"
      @mousedown="dragStart"
      @mouseup="dragStop"
      @mouseleave="dragStop"
      @touchstart="dragStart"
      @touchmove="dragging"
      @touchend="dragStop"
      ref="videoSliderRef"
      :style="{maxWidth: maxWidthCarousel}"
    >
      <div v-if="typeItems === 'video'" class="video">
        <UiSliderVideo
          :items="items"
          :videoRange="videoRange"
          :isReload="isReload"
          :isDragging="isDragging"
        />
      </div>
      <div v-else-if="typeItems === 'images'" class="images">
        <UiSliderImages
          :items="items"
          :isDragging="isDragging"
          @full-screen="handleFullScreen"
        />
      </div>
      <div v-else-if="typeItems === 'tour'" class="tour">
        <UiSliderTour :items="items" />
      </div>
    </div>
    <div
      v-if="typeItems !== 'tour' && !isMobile"
      class="wrapper__right-bar"
      id="wrapper__right-bar"
    >
      <div v-if="typeItems === 'video'">
        <div
          v-for="(poster, index) in posters"
          class="images__wrapper-video"
          @click="chooseItemActive(poster.videoSrc, index)"
        >
          <SvgBtnPlaySvg color="var(--var-blue-100)" />
          <img
            :src="poster.value"
            class="images__wrapper_poster"
            :class="{item_active: choosenItem === index}"
          />
        </div>
      </div>
      <div v-else-if="typeItems === 'images'">
        <div
          v-for="(images, index) in items"
          @click="chooseItemActive(images, index)"
          class="images__wrapper"
        >
          <div class="loader__wrapper">
            <UiLoader
              :radius="10"
              color="var(--var-blue-100)"
              :circle-width="2"
            />
          </div>
          <img
            v-if="!isReload"
            :src="images"
            :class="{item_active: choosenItem === index}"
          />
        </div>
      </div>
    </div>
    <UiModalsFullScreenModal
      v-if="isOpenFullScreenModal"
      :typeItems="typeItems"
      :items="items"
      :videoRange="videoRange"
      :posters="posters"
      :item-active="choosenItem"
      @close-action="closeActionHandler"
    />
  </div>
</template>

<script setup lang="ts">
import {ref, type ComputedRef} from 'vue';
import {useBaseStore} from '~/modules/BaseModule/base.store';

const props = withDefaults(
  defineProps<{
    items: any;
    posters: {videoSrc: string; value: string}[];
    typeItems: 'images' | 'video' | 'tour';
    isReload?: boolean;
    maxWidthCarousel?: string;
  }>(),
  {
    items: [],
    maxWidthCarousel: '850px',
  },
);
const base = useBaseStore();
const isMobile: ComputedRef<boolean> = computed(() => base.isMobile);
const videoRange = ref<null | number[]>(null);
const loadVideoForStart = ref<any>({});
const videoSliderRef = ref<null | HTMLElement>();
const iconLeft = ref<null | HTMLElement>();
const iconRight = ref<null | HTMLElement>();

let positionDiff: number = 0;
let firstImgWidth: number;
let isDragStart: boolean = false;
let prevPageX: number;
let prevScrollLeft: number;
const isDragging = ref<boolean>(false);
const choosenItem = ref<number>(0);
const isCanSlide = ref<boolean>(true);
const isOpenFullScreenModal = ref<boolean>(false);

const closeActionHandler = () => {
  isOpenFullScreenModal.value = false;
};

const handleFullScreen = (value: boolean): void => {
  isOpenFullScreenModal.value = value;
};

const showHideIcons = (isNeedChoosenItem?: boolean) => {
  if (videoSliderRef.value) {
    if (!isNeedChoosenItem) {
      choosenItem.value = Math.round(
        videoSliderRef.value.scrollLeft / firstImgWidth,
      );
    }

    if (iconLeft.value) {
      iconLeft.value.style.display =
        choosenItem.value <= 0 ? 'none' : 'block';
    }

    if (iconRight.value) {
      iconRight.value.style.display =
        choosenItem.value >= props.items.length - 1
          ? 'none'
          : 'block';
    }
  }
};

const changeSlideLeft = () => {
  if (videoSliderRef.value && isCanSlide.value) {
    isCanSlide.value = false;
    videoSliderRef.value.scrollLeft += -firstImgWidth;
    setTimeout(() => {
      showHideIcons();
      isCanSlide.value = true;
    }, 600);
  }
};

const changeSlideRight = () => {
  if (props.typeItems === 'video') {
    props.items.forEach((video: Array<string>, index: number) => {
      const videoTag: HTMLVideoElement | null =
        document.getElementById(video[0]) as HTMLVideoElement;
      if (!videoTag?.src) {
        const videoSplit = video[0].split('.');
        videoTag.src = isMobile.value
          ? `${videoSplit[0]}.${videoSplit[1]}_mobile.${videoSplit[2]}`
          : video[0];
      }
    });
  }

  if (videoSliderRef.value && isCanSlide.value) {
    isCanSlide.value = false;
    videoSliderRef.value.scrollLeft += firstImgWidth;
    setTimeout(() => {
      showHideIcons();
      isCanSlide.value = true;
    }, 600);
  }
};

const chooseItemActive = (activeItem: string, index: number) => {
  choosenItem.value = index;
  const choosenItemById: HTMLVideoElement | HTMLImageElement | null =
    document.getElementById(activeItem) as
      | HTMLVideoElement
      | HTMLImageElement;
  if (props.typeItems === 'video') {
    const videoSplit = activeItem.split('.');
    if (!choosenItemById?.src) {
      choosenItemById.src = isMobile.value
        ? `${videoSplit[0]}.${videoSplit[1]}_mobile.${videoSplit[2]}`
        : activeItem;
      setTimeout(async () => {
        if (choosenItemById instanceof HTMLVideoElement) {
          await choosenItemById.play();
        }
      }, 3000);
    }
  }

  if (choosenItemById) {
    new Promise((res) => {
      if (videoSliderRef.value) {
        videoSliderRef.value.scrollLeft = choosenItemById?.offsetLeft;
      }
      res('');
    }).then(() => {
      setTimeout(() => showHideIcons(true), 1300);
    });
  }
};

const dragging = (e: MouseEvent | TouchEvent) => {
  if (videoSliderRef.value) {
    if (!isDragStart) return;
    e.preventDefault();
    isDragging.value = true;
    videoSliderRef.value.classList.add('dragging');
    positionDiff =
      e instanceof MouseEvent
        ? e.pageX
        : e.touches[0].pageX - prevPageX;
    videoSliderRef.value.scrollLeft = prevScrollLeft - positionDiff;
  }
};

const autoSlide = () => {
  if (videoRange.value?.length !== props.items?.length) {
    videoRange.value = Array.from(Array(props.items.length).keys());
  }
  if (props.typeItems === 'video') {
    props.items.forEach((video: Array<string>, index: number) => {
      if (index >= 2 && !loadVideoForStart.value[video[0]]) {
        const videoTag: HTMLVideoElement | null =
          document.getElementById(video[0]) as HTMLVideoElement;
        if (videoTag) {
          const videoSplit: Array<string> = video[0].split('.');
          videoTag.src = isMobile.value
            ? `${videoSplit[0]}.${videoSplit[1]}_mobile.${videoSplit[2]}`
            : video[0];
        }
      }
    });
  }
  positionDiff = Math.abs(positionDiff);
  const additionalValueML = isMobile.value ? 10 : 14;

  if (videoSliderRef.value) {
    let firstImgWidth =
      videoSliderRef.value.clientWidth + additionalValueML;
    let valDifference = firstImgWidth - positionDiff;

    if (videoSliderRef.value.scrollLeft > prevScrollLeft) {
      return (videoSliderRef.value.scrollLeft +=
        positionDiff > firstImgWidth / 5
          ? valDifference
          : -positionDiff);
    }
    return (videoSliderRef.value.scrollLeft -=
      positionDiff > firstImgWidth / 5
        ? valDifference
        : -positionDiff);
  }
};

const dragStart = (e: MouseEvent | TouchEvent) => {
  if (videoSliderRef.value) {
    isDragStart = true;
    prevPageX =
      e instanceof MouseEvent ? e.pageX : e.touches[0].pageX;
    prevScrollLeft = videoSliderRef.value.scrollLeft;
  }
};

const dragStop = (e: MouseEvent | TouchEvent) => {
  isDragStart = false;
  if (videoRange.value?.length !== props.items?.length) {
    videoRange.value = Array.from(Array(props.items.length).keys());
  }
  if (videoSliderRef.value) {
    videoSliderRef.value.classList.remove('dragging');
    if (!isDragging.value) return;
    setTimeout(() => {
      isDragging.value = false;
    }, 0);
    new Promise((res, rej) => {
      const afterSlide = autoSlide();
      if (afterSlide && videoSliderRef.value) {
        if (afterSlide % firstImgWidth !== 0) {
          videoSliderRef.value.scrollLeft =
            firstImgWidth *
            Math.round(
              videoSliderRef.value.scrollLeft / firstImgWidth,
            );
        }
        res('');
      }
    }).then(() => {
      setTimeout(() => showHideIcons(), 600);
    });
  }
};

watch(
  () => [props.typeItems, isMobile.value],
  () => {
    if (videoSliderRef.value) {
      if (props.typeItems === 'video') {
        setTimeout(() => {
          props.items.forEach(
            (video: Array<string>, index: number) => {
              if (index <= 1) {
                const videoTag: HTMLVideoElement | null =
                  document.getElementById(
                    video[0],
                  ) as HTMLVideoElement;
                if (videoTag) {
                  const videoSplit = video[0].split('.');
                  videoTag.src = isMobile.value
                    ? `${videoSplit[0]}.${videoSplit[1]}_mobile.${videoSplit[2]}`
                    : video[0];
                }
              }
            },
          );
        }, 100);

        if (isMobile.value) {
          videoRange.value = [0, 1];
        } else {
          videoRange.value = Array.from(
            Array(props.items.length).keys(),
          );
        }
      } else {
        loadVideoForStart.value = {};
      }
      videoSliderRef.value.scrollLeft = 0;
      resizeHandler();
      setTimeout(() => showHideIcons(), 600);
    }
  },
);

function resizeHandler() {
  if (videoSliderRef.value) {
    const additionalValueML = isMobile.value ? 10 : 14;
    firstImgWidth =
      videoSliderRef.value.clientWidth + additionalValueML;
    const afterSlide = autoSlide();
    if (afterSlide && afterSlide % firstImgWidth !== 0) {
      videoSliderRef.value.scrollLeft =
        firstImgWidth *
        Math.round(videoSliderRef.value.scrollLeft / firstImgWidth);
    }
  }
}

onMounted(() => {
  if (isMobile.value) {
    videoRange.value = [0, 1];
  } else {
    videoRange.value = Array.from(Array(props.items.length).keys());
  }
  resizeHandler();
  setTimeout(() => showHideIcons(), 60);

  window.addEventListener('resize', resizeHandler);
});
onUnmounted(() => {
  window.removeEventListener('resize', resizeHandler);
});
</script>

<style scoped lang="scss">
.images__wrapper {
  position: relative;
  height: fit-content;
  min-height: 55px;
  .loader__wrapper {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100px;
    height: 55px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: -1;

    @media (max-width: $lg) {
      display: none;
    }
  }
}

.images__wrapper-video {
  position: relative;
  height: fit-content;
  min-height: 55px;

  svg {
    position: absolute;
    top: 24%;
    left: 30%;
    height: 30px;
    cursor: pointer;
  }
  .loader__wrapper {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100px;
    height: 55px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: -1;
  }
}
.gallery-carusel-wrp {
  display: flex;
  gap: 16px;

  @media (max-width: $lg) {
    flex-direction: column;
  }

  @media (max-width: 900px) {
    .gallery-carusel-wrp {
      .panorama-wrp {
        .panorama-item {
          border-radius: 8px;
          border: none;
        }
        .panorama-item {
          width: 100%;
        }
      }
    }
  }
}
.wrapper {
  max-width: 100vw;
  width: 100%;
  position: relative;
  display: flex;
  gap: $g-6;

  & span {
    top: 50%;
    height: 44px;
    width: 44px;
    color: #343f4f;
    font-size: 1.15rem;
    position: absolute;
    text-align: center;
    line-height: 44px;
    border-radius: 50%;
    transform: translateY(-50%);
    transition: transform 0.1s linear;

    z-index: 100;
    &:active {
      transform: translateY(-50%) scale(0.9);
    }
    &:hover {
      transition: all linear 0.2s;
      background: #a2a2a2;
    }
    svg {
      height: 44px;
      width: 44px;
    }
  }

  &__btn-left {
    left: 12px;
    display: none;
    justify-content: center;
    align-items: center;
  }
  &__btn-right {
    right: 290px;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: 1100px) {
      left: 780px;
    }
    @media (max-width: 900px) {
      right: 12px;
      left: auto;
    }
  }

  &__right-bar {
    min-width: 100px;
    max-width: 100px;
    display: flex;
    flex-direction: column;
    gap: $g-4;
    video {
      width: 100px;
      min-height: 55px;
      border-radius: $br-1;
      cursor: pointer;
    }
    img {
      width: 100px;
      min-height: 55px;
      border-radius: $br-1;
      cursor: pointer;
    }
    .item_active {
      border-radius: $br-2;
      border: 5px solid var(--var-blue-100);
      height: fit-content;
    }
  }
  .carousel {
    position: relative;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    scroll-behavior: smooth;
    border-radius: $br-3;

    .video {
      position: relative;
    }

    .images {
      position: relative;
    }
  }
  .dragging {
    scroll-behavior: auto;

    &img {
      pointer-events: none;
      cursor: grab !important;
    }
  }
}
.dragging {
  video {
    cursor: grabbing !important;
  }
  img {
    cursor: grabbing !important;
  }
}
</style>
